import React from 'react';

import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'src/common/components/tfa-auth/contexts/authContext';
import { Pages } from 'src/common/constants/Pages';
import { useUserRole } from 'src/common/hooks/useUserRole';
import SnackbarUtils from 'src/common/utils/SnackbarUtilsConfigurator';

import {
  useEnableUserMutation,
  useDisableUserMutation,
  useRemoveUserFromOrganizationMutation,
} from '../../../../common/apis/AdminApi';
import { ReactComponent as MedicalIconBlue } from '../../../../common/assets/icons/medical_icon_blue.svg';
import { ReactComponent as MessageIconBlue } from '../../../../common/assets/icons/message_icon_blue.svg';
import { ReactComponent as PhoneIconBlue } from '../../../../common/assets/icons/phone_icon_blue.svg';
import { ReactComponent as StarIconBlue } from '../../../../common/assets/icons/star_icon_blue.svg';
import { ReactComponent as UserAvatarIcon } from '../../../../common/assets/icons/user_avatar_icon.svg';
import UserIconGreen from '../../../../common/assets/icons/user_icon_green.svg';
import UserIconOrange from '../../../../common/assets/icons/user_icon_orange.svg';
import { ReactComponent as WorkIconBlue } from '../../../../common/assets/icons/work_icon_blue.svg';
import { User, UserRoles } from '../../../../common/types/User';
import InfoModal from '../../../components/InfoModal/InfoModal';

import '../../../../common/styles/main.scss';
import { LoadingButton } from '@mui/lab';

type RowProps = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  text: string;
};

const RowItem: React.FC<RowProps> = ({ Icon, text }) => (
  <Box
    className='row'
    sx={{
      marginBottom: '16px',
    }}
  >
    <Icon
      style={{
        minWidth: '20px',
      }}
    />
    <Typography
      fontFamily={'SFProDisplay-Regular'}
      sx={{ marginLeft: '16px', wordBreak: 'break-all' }}
    >
      {text}
    </Typography>
  </Box>
);

type Props = {
  user: User;
  style?: React.CSSProperties;
};

const UserContainer: React.FC<Props> = ({ user, style }) => {
  const [openModal, setOpenModal] = React.useState(false);
  const [modalText, setModalText] = React.useState('');
  const [modalIsLoading, setModalIsLoading] = React.useState(false);
  const authContext = React.useContext(AuthContext);

  const [enableUserTrigger] = useEnableUserMutation();
  const [disableUserTrigger] = useDisableUserMutation();

  const [removeFromOrganization, { isLoading }] =
    useRemoveUserFromOrganizationMutation();

  const { userRole } = useUserRole();
  const navigate = useNavigate();

  const toggleUserStatus = async () => {
    const userId = user.id.toString();

    setModalIsLoading(true);
    setOpenModal(true);

    user.active
      ? setModalText('User Disabled!')
      : setModalText('User Enabled!');

    await (user.active
      ? disableUserTrigger(userId)
      : enableUserTrigger(userId));

    //deactivate user in cognito userPool
    try {
      await (user.active
        ? authContext.adminDisableUser(user.email)
        : authContext.adminEnableUser(user.email));
    } catch (error) {
      console.log(error);
    }

    // setTimeout so the loading won't disappear too quick making a UI glitch
    setTimeout(() => {
      setModalIsLoading(false);
    }, 350);
  };

  const onClickRemoveFromOrganization = async () => {
    removeFromOrganization({
      userId: user.id,
    })
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);
        if (userRole === UserRoles.ORG_OWNER) {
          navigate(Pages.users);
        }
      });
  };

  return (
    <Box
      style={style}
      className='user-content-container'
      sx={{
        minWidth: '160px',
      }}
    >
      <Box className='row'>
        <UserAvatarIcon />
        <Typography sx={{ marginLeft: '16px' }}>{user.fullName}</Typography>
      </Box>
      <Box className='user-content-divider' />
      <RowItem Icon={WorkIconBlue} text={user.role} />
      <RowItem Icon={StarIconBlue} text={user.speciality} />
      <RowItem Icon={MedicalIconBlue} text={user.practiceName} />
      <RowItem Icon={MessageIconBlue} text={user.email} />
      <RowItem Icon={PhoneIconBlue} text={user.phone} />

      {userRole === UserRoles.SUPER_ADMIN && (
        <Box
          className='row'
          sx={{ justifyContent: 'space-between', marginBottom: '16px' }}
        >
          <Button
            onClick={() => toggleUserStatus()}
            variant='contained'
            color={user.active ? 'warning' : 'success'}
            disableElevation
            sx={{
              width: '100%',
              height: 50,
              borderRadius: '15px',
              marginRight: '8px',
            }}
          >
            <Typography color='white'>
              {user.active ? 'Disable User' : 'Activate User'}
            </Typography>
          </Button>
        </Box>
      )}

      {user.organizationId && (
        <Box className='row' sx={{ justifyContent: 'space-between' }}>
          <LoadingButton
            onClick={() => onClickRemoveFromOrganization()}
            disabled={isLoading}
            color='error'
            variant='contained'
            disableElevation
            sx={{
              width: '100%',
              height: 50,
              borderRadius: '15px',
              marginRight: '8px',
            }}
          >
            <Typography color='white'>Remove from Organization</Typography>
          </LoadingButton>
        </Box>
      )}

      <InfoModal
        open={openModal}
        loading={modalIsLoading}
        onClose={() => setOpenModal(false)}
        iconSrc={user.active ? UserIconGreen : UserIconOrange}
        text={modalText}
      />
    </Box>
  );
};

export default UserContainer;
